import * as Sentry from "@sentry/vue";

// One build serves production and preprod, so Rails names the environment in the page.
export function sentryEnvironment() {
  return document.head.querySelector('meta[name="environment"]')?.content || import.meta.env.MODE;
}

export const config = {
  dsn: import.meta.env.VITE_SENTRY_DSN,
  release: __FRONTEND_VERSION__, // eslint-disable-line no-undef
  environment: sentryEnvironment(),
  ignoreErrors: [
    // A chunk the browser cannot fetch — a stale index.html asking for asset names
    // the current deploy no longer serves, or a request that simply failed.
    // chunkLoadRecovery reloads the page, so the error is already handled.
    "Failed to fetch dynamically imported module",
    "error loading dynamically imported module",
    "Importing a module script failed",
    "'text/html' is not a valid JavaScript MIME type",
    // A dropped connection is the user's network, not a bug. The apollo error link and
    // handleError both leave network errors alone already; unhandled rejections from
    // smart queries are the one path that still reaches Sentry. Chrome, then Firefox.
    "Failed to fetch",
    "NetworkError when attempting to fetch resource",
    // Signing out calls clearStore, which rejects every query still in flight. Apollo
    // hides the message behind an error code in production builds — code 42 in Apollo
    // Client 3.14, so an upgrade needs this checked against their invariantErrorCodes.
    "Store reset while query was in flight",
    /go\.apollo\.dev\/c\/err#.*%22message%22%3A42(%2C|%7D)/,
    "Unable to preload CSS",
  ],
  // Extension content scripts throw inside our page, and the global unhandledrejection handler would report them as ours.
  denyUrls: [/^chrome-extension:\/\//i, /^moz-extension:\/\//i, /^safari-web-extension:\/\//i, /^chrome:\/\//i],
  // `errorLink` in ~/vue-apollo and `handleError` both decide, deliberately, not to report
  // Apollo network failures. Neither predicate gets a say when an awaited query rejects inside
  // a watcher or a lifecycle hook: Vue hands that to `app.config.errorHandler`, and @sentry/vue
  // captures it there — as it does for an unawaited one via `unhandledrejection`. Drop the
  // failures where the request never got a response, which is a suspended laptop or a dropped
  // connection, and keep the ones that did, so a 5xx from our own gateway still reports.
  beforeSend(event, hint) {
    const { networkError } = hint?.originalException ?? {};

    if (networkError && networkError.statusCode === undefined) return null;

    return event;
  },
};

export function initSentry(app) {
  Sentry.init({ app, ...config });
  app.config.globalProperties.$sentry = Sentry;

  return Sentry;
}
